import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
    Alert, Box, Button, Checkbox, CircularProgress, Divider, FormControlLabel, IconButton,
    InputAdornment, Link, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import React from 'react';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';
import { BLUE_TEXT, BORDER, INTER, NAVY, PAGE_BACKGROUND, POPPINS, SLATE, cardSx, primaryButtonSx } from '../publicStyles';

type LoginState = 'default' | 'error' | 'passwordChanged' | 'signedIn';

const stateOptions: { value: LoginState; label: string }[] = [
    { value: 'default', label: 'Default' },
    { value: 'error', label: 'Login failed' },
    { value: 'passwordChanged', label: 'Password changed' },
    { value: 'signedIn', label: 'Already signed in' },
];

const linkSx = { fontFamily: INTER, fontSize: 14, fontWeight: 600, color: BLUE_TEXT } as const;

interface LoginPageProps {
    onNotice: (message: string) => void;
}

// Static prototype: nothing is sent to the server. An email containing "teacher" opens the
// teacher mockup; any other email opens the V2 student mockup.
export default function PublicLoginPage({ onNotice }: LoginPageProps) {
    const history = useHistory();
    const initialState = new URLSearchParams(useLocation().search).get('state');
    const [viewState, setViewState] = React.useState<LoginState>(
        stateOptions.some(option => option.value === initialState) ? initialState as LoginState : 'default');
    const [email, setEmail] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [showPassword, setShowPassword] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const errorRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (viewState === 'error') errorRef.current?.focus();
    }, [viewState]);

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setSubmitting(true);
        window.setTimeout(() => {
            setSubmitting(false);
            if (password === 'wrong') {
                setViewState('error');
                return;
            }
            history.push(email.toLowerCase().includes('teacher') ? '/mockup/teacher' : '/mockup/v2/student');
        }, 700);
    };

    return (
        <Box component="main" sx={{ flex: 1, backgroundColor: PAGE_BACKGROUND, px: 2, py: { xs: 4, sm: 7 } }}>
            <Box sx={{ maxWidth: 440, mx: 'auto' }}>
                <Box sx={{ ...cardSx, p: { xs: 3, sm: 4 }, boxShadow: '0 10px 30px rgba(22, 58, 99, 0.06)' }}>
                    {viewState === 'signedIn' ? (
                        <Stack spacing={2} sx={{ textAlign: 'center' }}>
                            <Typography component="h1" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 24, color: NAVY }}>You&apos;re already signed in</Typography>
                            <Typography sx={{ fontFamily: INTER, fontSize: 15, color: SLATE }}>
                                Signed in as <Box component="strong" sx={{ color: NAVY }}>emily.foster@example.edu</Box>
                            </Typography>
                            <Button onClick={() => history.push('/mockup/teacher')} sx={primaryButtonSx}>Continue to your classes</Button>
                            <Button variant="text" onClick={() => { setViewState('default'); onNotice('Signed out (mockup).'); }}>
                                Sign out and use another account
                            </Button>
                        </Stack>
                    ) : (
                        <>
                            <Typography component="h1" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 26, color: NAVY, textAlign: 'center' }}>
                                Log in
                            </Typography>
                            <Typography sx={{ fontFamily: INTER, fontSize: 15, color: SLATE, mt: 0.75, textAlign: 'center' }}>
                                Welcome back. Pick up where you left off.
                            </Typography>

                            {viewState === 'passwordChanged' && (
                                <Alert severity="success" role="status" sx={{ mt: 3, borderRadius: '12px' }}>
                                    Password updated. Log in with your new password.
                                </Alert>
                            )}
                            {viewState === 'error' && (
                                <Alert severity="error" ref={errorRef} tabIndex={-1} id="login-error" sx={{ mt: 3, borderRadius: '12px', outline: 'none' }}>
                                    That email and password don&apos;t match. Check them and try again, or reset your password.
                                </Alert>
                            )}

                            <Stack component="form" spacing={2.25} onSubmit={handleSubmit} noValidate={false} sx={{ mt: 3 }}>
                                <TextField
                                    label="Email"
                                    type="email"
                                    name="email"
                                    autoComplete="email"
                                    required
                                    fullWidth
                                    value={email}
                                    onChange={event => setEmail(event.target.value)}
                                    error={viewState === 'error'}
                                    inputProps={{ 'aria-describedby': viewState === 'error' ? 'login-error' : undefined }}
                                />
                                <Box>
                                    <TextField
                                        label="Password"
                                        type={showPassword ? 'text' : 'password'}
                                        name="password"
                                        autoComplete="current-password"
                                        required
                                        fullWidth
                                        value={password}
                                        onChange={event => setPassword(event.target.value)}
                                        error={viewState === 'error'}
                                        inputProps={{ 'aria-describedby': viewState === 'error' ? 'login-error' : undefined }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowPassword(value => !value)}
                                                        edge="end"
                                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                        aria-pressed={showPassword}
                                                    >
                                                        {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ mt: 0.5 }}>
                                        <FormControlLabel
                                            control={<Checkbox name="rememberMe" />}
                                            label={<Typography sx={{ fontFamily: INTER, fontSize: 14, color: NAVY }}>Keep me signed in</Typography>}
                                            sx={{ minHeight: 44, mr: 1 }}
                                        />
                                        <Link component={RouterLink} to="/mockup/public/forgot-password" underline="hover"
                                            sx={{ ...linkSx, minHeight: 44 }}>
                                            Forgot password?
                                        </Link>
                                    </Stack>
                                </Box>
                                <Button type="submit" disabled={submitting} sx={primaryButtonSx} aria-live="polite">
                                    {submitting
                                        ? <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} sx={{ color: 'inherit' }} /><span>Logging in…</span></Stack>
                                        : 'Log in'}
                                </Button>
                            </Stack>

                            <Divider sx={{ my: 3, borderColor: BORDER }} />
                            <Stack spacing={0.5} sx={{ textAlign: 'center' }}>
                                <Typography sx={{ fontFamily: INTER, fontSize: 14, color: SLATE }}>
                                    New student?{' '}
                                    <Link component={RouterLink} to="/mockup/public/signup" underline="hover" sx={linkSx}>
                                        Create an account
                                    </Link>
                                </Typography>
                                <Typography sx={{ fontFamily: INTER, fontSize: 14, color: SLATE }}>
                                    Teacher?{' '}
                                    <Link component={RouterLink} to="/mockup/public/pilot" underline="hover" sx={linkSx}>
                                        Request a pilot
                                    </Link>
                                </Typography>
                            </Stack>
                        </>
                    )}
                </Box>

                <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, textAlign: 'center', mt: 3 }}>
                    Need help?{' '}
                    <Link href="mailto:support@verbaschola.com" underline="hover" sx={{ ...linkSx, fontSize: 13 }}>support@verbaschola.com</Link>
                    {' · '}
                    <Link component={RouterLink} to="/mockup/public" underline="hover" sx={{ ...linkSx, fontSize: 13 }}>Back to home</Link>
                </Typography>

                <Box component="aside" aria-label="Mockup controls" sx={{ mt: 4, p: 2, border: `1px dashed ${BORDER}`, borderRadius: '14px' }}>
                    <Typography sx={{ fontFamily: INTER, fontSize: 12, fontWeight: 700, color: SLATE, mb: 1 }}>Mockup state (not part of the design)</Typography>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={viewState}
                        onChange={(_, value: LoginState | null) => value && setViewState(value)}
                        aria-label="Login page state"
                        sx={{ flexWrap: 'wrap', gap: 0.5, '& .MuiToggleButton-root': { border: `1px solid ${BORDER} !important`, borderRadius: '10px !important', fontSize: 12, px: 1.25 } }}
                    >
                        {stateOptions.map(option => <ToggleButton key={option.value} value={option.value}>{option.label}</ToggleButton>)}
                    </ToggleButtonGroup>
                    <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 1 }}>
                        Submitting opens the V2 student mockup, or the teacher mockup if the email contains “teacher”. Password “wrong” shows the failure state.
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
